import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import {
  trackCTA,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { WRS_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * The plan qualifier — the most important non-negotiable on this page.
 *
 * Everything the page describes is Heirloom-only, and not partially. Three
 * independent gates say so:
 *   - `PLAN_FEATURE_MATRIX.invitationProduct` is true for `pro` alone
 *     (api/constants/planFeatures.mjs), and it explicitly names RSVP, the guest
 *     list, event sections, send-invitations and seating as the same
 *     entitlement;
 *   - the whole RSVP router is mounted behind `requireProPlanWithCode`
 *     (api/routes/rsvp.routes.mjs), as is the whole guests router;
 *   - the public event endpoint strips the invitation payload for any other
 *     plan, so there is no page for the RSVP to live in either.
 *
 * A Blossom or Bouquet couple has no RSVP at all — not a smaller one — so a
 * page about collecting replies that leaves that ambiguous is a page that
 * mis-sells. Three statements in order, so a reader who takes only one of them
 * still cannot come away with the wrong idea:
 *
 *   1. the RSVP and everything it feeds are included with Heirloom,
 *   2. photo sharing is on every plan and the RSVP is NOT,
 *   3. features vary by plan — with a link to the plans.
 *
 * Named as a section (`heading-50`, its own impression) rather than a footnote,
 * because a plan qualifier that reads as fine print is one someone can honestly
 * say they missed.
 *
 * It is also where the page hands on to its three siblings. All three links are
 * contextual rather than decorative: this is the one section that talks about
 * what a plan contains rather than about what the RSVP does.
 */
const RsvpPlan = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WRS_SECTION.PLAN, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-editorial wps-editorial-solo">
          <div className="wps-editorial-text">
            <h2 className="heading-50">{t("weddingRsvp.plan.heading")}</h2>
            <p className="body-20">{t("weddingRsvp.plan.body1")}</p>
            <p className="body-20">{t("weddingRsvp.plan.body2")}</p>
            <p className="body-20">{t("weddingRsvp.plan.body3")}</p>
          </div>
        </div>

        <p className="wps-plan-note">
          {t("weddingRsvp.plan.note")}{" "}
          <a className="wps-text-link" href={localize("/#pricing")} onClick={scrollToPlans}>
            {t("weddingRsvp.plan.noteLink")}
          </a>
        </p>

        <div className="wpl-cross-links">
          <a
            className="wps-text-link"
            href={localize("/digital-wedding-invitation")}
            onClick={() =>
              trackCTA("wedding_rsvp_plan", {
                ...analyticsContext,
                ctaName: "digital_wedding_invitation",
              })
            }
          >
            {t("weddingRsvp.plan.invitationLink")}
          </a>
          {/*
            Contextual, not decorative: body1 has just named table planning as
            part of the same Heirloom entitlement, and seating is where the
            replies this page collects actually end up. /wedding-seating is the
            page that describes it.
          */}
          <a
            className="wps-text-link"
            href={localize("/wedding-seating")}
            onClick={() =>
              trackCTA("wedding_rsvp_plan", {
                ...analyticsContext,
                ctaName: "wedding_seating",
              })
            }
          >
            {t("weddingRsvp.plan.seatingLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/wedding-planning")}
            onClick={() =>
              trackCTA("wedding_rsvp_plan", {
                ...analyticsContext,
                ctaName: "wedding_planning",
              })
            }
          >
            {t("weddingRsvp.plan.planningLink")}
          </a>
          <a
            className="wps-text-link"
            href={localize("/wedding-photo-sharing")}
            onClick={() =>
              trackCTA("wedding_rsvp_plan", {
                ...analyticsContext,
                ctaName: "wedding_photo_sharing",
              })
            }
          >
            {t("weddingRsvp.plan.photoLink")}
          </a>
        </div>
      </div>
    </section>
  );
};

export default RsvpPlan;
